// SN-19938: a converted Form prints its tick boxes as text ("☐ NEW ☐ USED ☐ REBUILT"), so they stay inline
// in the PDF (form-print.utils.ts, CHECKBOX_PRINT_GLYPHS prints ☑ as a filled square). Clicking a box in a
// Form ticks or unticks it; a click on the words beside it does nothing.
import type { Editor } from "tinymce";

const TOGGLED: Readonly<Record<string, string>> = {
  "☐": "☑",
  "☑": "☐",
  "☒": "☐"
};

// How far outside the glyph's own box a click still counts, in px.
const HIT_SLOP_PX = 2;

/** The box glyph at a caret position (it sits just after or just before the caret), or null. */
export const boxGlyphAt = (node: Node | null, offset: number) => {
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  const { data } = node as Text;
  const at = [offset, offset - 1].find((index) => TOGGLED[data[index]]);
  return at === undefined ? null : { node: node as Text, at };
};

export const toggleBoxGlyph = (node: Text, at: number) =>
  node.replaceData(at, 1, TOGGLED[node.data[at]]);

const caretAt = (doc: Document, x: number, y: number) => {
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y);
    return position && { node: position.offsetNode, offset: position.offset };
  }
  const range = doc.caretRangeFromPoint?.(x, y);
  return range
    ? { node: range.startContainer, offset: range.startOffset }
    : null;
};

const isOnGlyph = (
  doc: Document,
  node: Text,
  at: number,
  x: number,
  y: number
) => {
  const range = doc.createRange();
  range.setStart(node, at);
  range.setEnd(node, at + 1);
  const box = range.getBoundingClientRect();
  return (
    x >= box.left - HIT_SLOP_PX &&
    x <= box.right + HIT_SLOP_PX &&
    y >= box.top - HIT_SLOP_PX &&
    y <= box.bottom + HIT_SLOP_PX
  );
};

export const addCheckboxToggle = (editor: Editor) => {
  editor.on("click", (event) => {
    const doc = editor.getDoc();
    const caret = caretAt(doc, event.clientX, event.clientY);
    const glyph = caret && boxGlyphAt(caret.node, caret.offset);
    if (
      !glyph ||
      !isOnGlyph(doc, glyph.node, glyph.at, event.clientX, event.clientY)
    )
      return;
    // One undo step, which also fires the editor's change event, so the form sees the tick.
    editor.undoManager.transact(() => toggleBoxGlyph(glyph.node, glyph.at));
    editor.nodeChanged();
  });
};
